import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { 
  Shield, CheckCircle, Clock, UserCircle, Buildings, Certificate, 
  MagnifyingGlass, Star, Coins, ChartBar, ClipboardText, Trophy,
  ArrowRight, Eye, FileText, Warning, CheckSquare
} from '@phosphor-icons/react';
import Navbar from '@/components/custom/Navbar';
import Footer from '@/components/custom/Footer';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuditorPortalPage = () => {
  const navigate = useNavigate();
  const [auditors, setAuditors] = useState([]);
  const [pendingAudits, setPendingAudits] = useState([]);
  const [myDashboard, setMyDashboard] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('directory');
  
  // Registration form
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    organization: '',
    certifications: '',
    regions: '',
    industries: ''
  });

  // Audit findings form
  const [findingsForm, setFindingsForm] = useState({
    audit_id: null,
    verified: true,
    findings_summary: '',
    score_adjustment: 0,
    recommendations: ''
  });

  // Check if user is an auditor
  const irisId = localStorage.getItem('ginsgood_iris_id');
  const irisType = localStorage.getItem('ginsgood_iris_type');
  const auditorId = localStorage.getItem('ginsgood_auditor_id');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [auditorsRes, pendingRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/auditors?verified_only=false`),
        axios.get(`${API}/audits/pending`).catch(() => ({ data: { pending_audits: [] } })),
        axios.get(`${API}/auditors/leaderboard?limit=10`).catch(() => ({ data: { leaderboard: [] } }))
      ]);
      
      setAuditors(auditorsRes.data.auditors || []);
      setPendingAudits(pendingRes.data.pending_audits || []);
      setLeaderboard(leaderboardRes.data.leaderboard || []);

      // If user is an auditor, fetch dashboard
      if (auditorId) {
        try {
          const dashRes = await axios.get(`${API}/auditors/${auditorId}/dashboard`);
          setMyDashboard(dashRes.data);
        } catch (e) {
          console.log('Not an auditor or dashboard error');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: registerData.name,
        email: registerData.email,
        organization: registerData.organization,
        certifications: registerData.certifications.split(',').map(s => s.trim()).filter(Boolean),
        regions: registerData.regions.split(',').map(s => s.trim()).filter(Boolean),
        industries: registerData.industries.split(',').map(s => s.trim()).filter(Boolean)
      };
      
      const response = await axios.post(`${API}/auditors/register`, null, { params: payload });
      if (response.data.success) {
        localStorage.setItem('ginsgood_auditor_id', response.data.auditor_id);
        toast.success('Registration submitted! Awaiting verification.');
        setRegisterData({ name: '', email: '', organization: '', certifications: '', regions: '', industries: '' });
        fetchData();
        setActiveTab('directory');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const handleClaimAudit = async (auditId) => {
    if (!auditorId) {
      toast.error('Please register as an auditor first');
      setActiveTab('register');
      return;
    }
    
    try {
      const response = await axios.put(`${API}/audits/${auditId}/claim?auditor_id=${auditorId}`);
      if (response.data.success) {
        toast.success('Audit claimed successfully!');
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim audit');
    }
  };

  const handleSubmitFindings = async (e) => {
    e.preventDefault();
    if (!findingsForm.audit_id) return;

    try {
      const response = await axios.put(
        `${API}/audits/${findingsForm.audit_id}/submit-findings`,
        null,
        {
          params: {
            auditor_id: auditorId,
            verified: findingsForm.verified,
            findings_summary: findingsForm.findings_summary,
            score_adjustment: parseFloat(findingsForm.score_adjustment) || 0,
            recommendations: findingsForm.recommendations.split('\n').filter(Boolean)
          }
        }
      );
      
      if (response.data.success) {
        toast.success(`Audit completed! Earned ${response.data.gsg_earned} GSG`);
        setFindingsForm({ audit_id: null, verified: true, findings_summary: '', score_adjustment: 0, recommendations: '' });
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit findings');
    }
  };

  const filteredAuditors = auditors.filter(a => 
    a.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.organization?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">
              <Shield weight="duotone" className="w-4 h-4 mr-2" />
              Third-Party Verification
            </Badge>
            <h1 className="text-3xl md:text-4xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Auditor Marketplace
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our network of certified auditors verify sustainability claims, ensuring trust and transparency in every certification.
            </p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-4">
              <TabsTrigger value="directory" data-testid="tab-directory">Directory</TabsTrigger>
              <TabsTrigger value="assignments" data-testid="tab-assignments">Open Audits</TabsTrigger>
              {auditorId && <TabsTrigger value="dashboard" data-testid="tab-dashboard">My Dashboard</TabsTrigger>}
              <TabsTrigger value="register" data-testid="tab-register">Become Auditor</TabsTrigger>
            </TabsList>

            {/* Directory Tab */}
            <TabsContent value="directory">
              {/* Search */}
              <div className="relative max-w-md mx-auto mb-8">
                <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Search auditors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-12 rounded-full"
                  data-testid="search-auditors"
                />
              </div>

              {/* Leaderboard Banner */}
              {leaderboard.length > 0 && (
                <Card className="p-6 mb-8 bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200">
                  <div className="flex items-center gap-3 mb-4">
                    <Trophy weight="duotone" className="w-6 h-6 text-yellow-600" />
                    <h3 className="font-medium">Top Auditors</h3>
                  </div>
                  <div className="flex gap-4 overflow-x-auto pb-2">
                    {leaderboard.slice(0, 5).map((auditor, idx) => (
                      <div key={auditor.id} className="flex items-center gap-3 bg-white rounded-lg p-3 min-w-[200px]">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                          idx === 1 ? 'bg-gray-100 text-gray-700' :
                          idx === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-muted'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{auditor.name}</p>
                          <div className="flex items-center gap-1 text-sm">
                            <Star weight="fill" className="w-3 h-3 text-yellow-500" />
                            <span>{auditor.rating || 0}</span>
                            <span className="text-muted-foreground">({auditor.total_audits} audits)</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Auditors Grid */}
              {loading ? (
                <div className="text-center py-12 text-muted-foreground">Loading auditors...</div>
              ) : filteredAuditors.length === 0 ? (
                <Card className="p-12 text-center">
                  <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">No Auditors Found</h3>
                  <p className="text-muted-foreground">Be the first to join our auditor network!</p>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredAuditors.map(auditor => (
                    <Card key={auditor.id} className="p-6 hover:shadow-lg transition-shadow" data-testid={`auditor-${auditor.id}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                          <UserCircle weight="duotone" className="w-6 h-6 text-primary" />
                        </div>
                        <Badge variant={auditor.verified ? "default" : "secondary"}>
                          {auditor.verified ? (
                            <><CheckCircle className="w-3 h-3 mr-1" /> Verified</>
                          ) : (
                            <><Clock className="w-3 h-3 mr-1" /> Pending</>
                          )}
                        </Badge>
                      </div>
                      
                      <h3 className="font-medium text-lg mb-1">{auditor.name}</h3>
                      <p className="text-sm text-muted-foreground mb-4 flex items-center gap-1">
                        <Buildings className="w-4 h-4" />
                        {auditor.organization}
                      </p>

                      {auditor.rating > 0 && (
                        <div className="flex items-center gap-1 mb-3">
                          <Star weight="fill" className="w-4 h-4 text-yellow-500" />
                          <span className="font-medium">{auditor.rating}</span>
                          <span className="text-muted-foreground text-sm">({auditor.total_audits} audits)</span>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-1 mb-4">
                        {auditor.certifications?.slice(0, 3).map((cert, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{cert}</Badge>
                        ))}
                      </div>

                      <div className="text-xs text-muted-foreground">
                        <p><strong>Industries:</strong> {auditor.industries?.join(', ') || 'All'}</p>
                        <p><strong>Regions:</strong> {auditor.regions?.join(', ') || 'Global'}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Open Audits Tab */}
            <TabsContent value="assignments">
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-xl font-medium mb-2">Open Audit Requests</h2>
                  <p className="text-muted-foreground">Claim audits to verify sustainability certifications and earn GSG</p>
                </div>

                {pendingAudits.length === 0 ? (
                  <Card className="p-12 text-center">
                    <ClipboardText className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-medium mb-2">No Open Audits</h3>
                    <p className="text-muted-foreground">Check back later for new audit requests</p>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 gap-6">
                    {pendingAudits.map(audit => (
                      <Card key={audit.id} className="p-6" data-testid={`audit-${audit.id}`}>
                        <div className="flex items-start justify-between mb-4">
                          <Badge variant="outline">{audit.audit_type} Audit</Badge>
                          <Badge variant="secondary">
                            <Coins className="w-3 h-3 mr-1" />
                            100+ GSG
                          </Badge>
                        </div>

                        {audit.certificate && (
                          <div className="mb-4">
                            <h3 className="font-medium">{audit.certificate.product_name}</h3>
                            <p className="text-sm text-muted-foreground">by {audit.certificate.artisan_name}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-sm">Score: {audit.certificate.ginsgood_score}%</span>
                              <Badge style={{ backgroundColor: audit.certificate.certification_tier?.color }}>
                                {audit.certificate.certification_tier?.name}
                              </Badge>
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => navigate(`/passport/${audit.certificate_id}`)}
                            className="flex-1"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Button 
                            size="sm" 
                            onClick={() => handleClaimAudit(audit.id)}
                            className="flex-1"
                            data-testid={`claim-${audit.id}`}
                          >
                            Claim Audit
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Dashboard Tab */}
            {auditorId && (
              <TabsContent value="dashboard">
                {myDashboard ? (
                  <div className="space-y-8">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="p-4 text-center">
                        <ChartBar className="w-8 h-8 mx-auto mb-2 text-primary" />
                        <p className="text-2xl font-bold">{myDashboard.stats?.total_audits || 0}</p>
                        <p className="text-sm text-muted-foreground">Total Audits</p>
                      </Card>
                      <Card className="p-4 text-center">
                        <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                        <p className="text-2xl font-bold">{myDashboard.stats?.completed || 0}</p>
                        <p className="text-sm text-muted-foreground">Completed</p>
                      </Card>
                      <Card className="p-4 text-center">
                        <Star className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                        <p className="text-2xl font-bold">{myDashboard.stats?.rating || 0}</p>
                        <p className="text-sm text-muted-foreground">Rating</p>
                      </Card>
                      <Card className="p-4 text-center">
                        <Coins className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                        <p className="text-2xl font-bold">{myDashboard.stats?.gsg_balance || 0}</p>
                        <p className="text-sm text-muted-foreground">GSG Earned</p>
                      </Card>
                    </div>

                    {/* In Progress Audits */}
                    {myDashboard.stats?.in_progress > 0 && (
                      <Card className="p-6">
                        <h3 className="font-medium mb-4 flex items-center gap-2">
                          <Clock className="w-5 h-5" />
                          In Progress ({myDashboard.stats.in_progress})
                        </h3>
                        <div className="space-y-4">
                          {myDashboard.recent_assignments
                            ?.filter(a => a.status === 'in_progress')
                            .map(audit => (
                              <Card key={audit.id} className="p-4 bg-yellow-50 border-yellow-200">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium">Audit #{audit.id.slice(0, 8)}</p>
                                    <p className="text-sm text-muted-foreground">{audit.audit_type} audit</p>
                                  </div>
                                  <Button 
                                    size="sm"
                                    onClick={() => setFindingsForm({ ...findingsForm, audit_id: audit.id })}
                                    data-testid={`submit-findings-${audit.id}`}
                                  >
                                    Submit Findings
                                  </Button>
                                </div>
                              </Card>
                            ))}
                        </div>
                      </Card>
                    )}

                    {/* Submit Findings Modal */}
                    {findingsForm.audit_id && (
                      <Card className="p-6 border-2 border-primary">
                        <h3 className="font-medium mb-4 flex items-center gap-2">
                          <FileText className="w-5 h-5" />
                          Submit Audit Findings
                        </h3>
                        <form onSubmit={handleSubmitFindings} className="space-y-4">
                          <div>
                            <Label>Verification Result</Label>
                            <div className="flex gap-4 mt-2">
                              <Button
                                type="button"
                                variant={findingsForm.verified ? "default" : "outline"}
                                onClick={() => setFindingsForm({ ...findingsForm, verified: true })}
                                className="flex-1"
                              >
                                <CheckSquare className="w-4 h-4 mr-2" />
                                Verified
                              </Button>
                              <Button
                                type="button"
                                variant={!findingsForm.verified ? "destructive" : "outline"}
                                onClick={() => setFindingsForm({ ...findingsForm, verified: false })}
                                className="flex-1"
                              >
                                <Warning className="w-4 h-4 mr-2" />
                                Issues Found
                              </Button>
                            </div>
                          </div>

                          <div>
                            <Label>Findings Summary *</Label>
                            <Textarea
                              value={findingsForm.findings_summary}
                              onChange={(e) => setFindingsForm({ ...findingsForm, findings_summary: e.target.value })}
                              placeholder="Describe your audit findings..."
                              rows={4}
                              required
                            />
                          </div>

                          <div>
                            <Label>Score Adjustment (-10 to +10)</Label>
                            <Input
                              type="number"
                              min="-10"
                              max="10"
                              step="0.5"
                              value={findingsForm.score_adjustment}
                              onChange={(e) => setFindingsForm({ ...findingsForm, score_adjustment: e.target.value })}
                            />
                          </div>

                          <div>
                            <Label>Recommendations (one per line)</Label>
                            <Textarea
                              value={findingsForm.recommendations}
                              onChange={(e) => setFindingsForm({ ...findingsForm, recommendations: e.target.value })}
                              placeholder="Enter recommendations for improvement..."
                              rows={3}
                            />
                          </div>

                          <div className="flex gap-3">
                            <Button 
                              type="button" 
                              variant="outline"
                              onClick={() => setFindingsForm({ audit_id: null, verified: true, findings_summary: '', score_adjustment: 0, recommendations: '' })}
                              className="flex-1"
                            >
                              Cancel
                            </Button>
                            <Button type="submit" className="flex-1">
                              Submit Findings
                            </Button>
                          </div>
                        </form>
                      </Card>
                    )}

                    {/* Recent Activity */}
                    <Card className="p-6">
                      <h3 className="font-medium mb-4">Recent Activity</h3>
                      {myDashboard.recent_assignments?.length === 0 ? (
                        <p className="text-muted-foreground text-center py-4">No audit history yet</p>
                      ) : (
                        <div className="space-y-3">
                          {myDashboard.recent_assignments?.slice(0, 5).map(audit => (
                            <div key={audit.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                              <div className="flex items-center gap-3">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  audit.status === 'completed' ? 'bg-green-100' : 
                                  audit.status === 'in_progress' ? 'bg-yellow-100' : 'bg-gray-100'
                                }`}>
                                  {audit.status === 'completed' ? (
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                  ) : (
                                    <Clock className="w-5 h-5 text-yellow-600" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium">Audit #{audit.id.slice(0, 8)}</p>
                                  <p className="text-xs text-muted-foreground">{audit.audit_type}</p>
                                </div>
                              </div>
                              <Badge variant={audit.status === 'completed' ? 'default' : 'secondary'}>
                                {audit.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  </div>
                ) : (
                  <Card className="p-12 text-center">
                    <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">Loading dashboard...</p>
                  </Card>
                )}
              </TabsContent>
            )}

            {/* Register Tab */}
            <TabsContent value="register">
              <Card className="max-w-xl mx-auto p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Certificate weight="duotone" className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-xl font-medium mb-2">Join the Auditor Network</h2>
                  <p className="text-muted-foreground text-sm">
                    Help verify sustainability claims and earn GSG tokens for every completed audit.
                  </p>
                </div>

                {/* Benefits */}
                <div className="grid grid-cols-3 gap-4 mb-8">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <Coins className="w-6 h-6 mx-auto mb-1 text-yellow-500" />
                    <p className="text-sm font-medium">100+ GSG</p>
                    <p className="text-xs text-muted-foreground">per audit</p>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <Star className="w-6 h-6 mx-auto mb-1 text-yellow-500" />
                    <p className="text-sm font-medium">Build Rating</p>
                    <p className="text-xs text-muted-foreground">reputation</p>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <Trophy className="w-6 h-6 mx-auto mb-1 text-yellow-500" />
                    <p className="text-sm font-medium">Leaderboard</p>
                    <p className="text-xs text-muted-foreground">recognition</p>
                  </div>
                </div>

                <form onSubmit={handleRegister} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        placeholder="Dr. Jane Smith"
                        required
                        data-testid="register-name"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        placeholder="jane@auditor.com"
                        required
                        data-testid="register-email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Organization *</Label>
                    <Input
                      value={registerData.organization}
                      onChange={(e) => setRegisterData({ ...registerData, organization: e.target.value })}
                      placeholder="Global Sustainability Auditors"
                      required
                      data-testid="register-organization"
                    />
                  </div>

                  <div>
                    <Label>Certifications (comma-separated)</Label>
                    <Input
                      value={registerData.certifications}
                      onChange={(e) => setRegisterData({ ...registerData, certifications: e.target.value })}
                      placeholder="ISO 14001, GOTS, Fair Trade"
                      data-testid="register-certifications"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Regions (comma-separated)</Label>
                      <Input
                        value={registerData.regions}
                        onChange={(e) => setRegisterData({ ...registerData, regions: e.target.value })}
                        placeholder="South Asia, Europe"
                        data-testid="register-regions"
                      />
                    </div>
                    <div>
                      <Label>Industries (comma-separated)</Label>
                      <Input
                        value={registerData.industries}
                        onChange={(e) => setRegisterData({ ...registerData, industries: e.target.value })}
                        placeholder="textiles, jewelry"
                        data-testid="register-industries"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-12 rounded-full" data-testid="submit-register">
                    Submit Application
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AuditorPortalPage;
